import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { getStorage, ref, uploadBytesResumable } from '@firebase/storage';
import { AdminService } from '../../services/admin';
import { AlertService } from '../../../../core/ui/alert';
import { LoaderService } from '../../../../core/ui/loader';
import { Liquidation } from '../../../../shared/models/liquidation';

export interface LiquidationFormDialogData {
    liquidation?: Liquidation;
}

@Component({
    selector: 'app-liquidation-form',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MatDialogTitle, MatDialogContent, MatDialogActions, MatButtonModule, MatFormFieldModule, MatInputModule, MatIconModule],
    templateUrl: './liquidation-form.html'
})
export class LiquidationForm implements OnInit {
    form!: FormGroup;
    isEditMode = false;
    liquidationId: string | null = null;

    isUploading = false;
    isProcessing = false;
    excelUploaded = false;
    uploadedFileName: string | null = null;
    showExcelError = false;

    get isNew(): boolean { return !this.isEditMode; }
    get hasExistingExcel(): boolean { return !!this.data.liquidation?.excelFileName; }
    get excelReady(): boolean { return this.excelUploaded || (this.hasExistingExcel && !this.isUploading); }
    get currentExcelName(): string | null {
        if (this.uploadedFileName) return this.uploadedFileName;
        if (this.data.liquidation?.excelFileName) return this.data.liquidation.excelFileName;
        return null;
    }
    get canProcess(): boolean { return this.form.valid && this.excelReady && !this.isProcessing && !this.isUploading; }

    constructor(
        private fb: FormBuilder,
        private adminService: AdminService,
        private alertService: AlertService,
        private loaderService: LoaderService,
        @Inject(MAT_DIALOG_DATA) public data: LiquidationFormDialogData,
        public dialogRef: MatDialogRef<LiquidationForm>
    ) {}

    ngOnInit(): void {
        this.isEditMode = !!this.data.liquidation;
        this.liquidationId = this.data.liquidation?.id ?? null;

        this.form = this.fb.group({
            week:      [this.data.liquidation?.week ?? null, [Validators.required, Validators.min(1), Validators.max(60)]],
            year:      [this.data.liquidation?.year ?? null, [Validators.required, Validators.min(2000)]],
            daysOfWeek:[this.data.liquidation?.daysOfWeek ?? null, [Validators.required, Validators.min(1)]]
        });
    }

    async onFileSelected(event: Event): Promise<void> {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.alertService.error('Completa todos los campos requeridos antes de cargar el archivo.', 'Campos requeridos');
            input.value = '';
            return;
        }

        this.isUploading = true;
        this.loaderService.show();

        try {
            if (!this.liquidationId) {
                const { week, year, daysOfWeek } = this.form.value;
                const newId = await this.adminService.createLiquidation({ week, year, daysOfWeek }, false);
                if (!newId) return;
                this.liquidationId = newId;
            }

            const uploaded = await this.uploadExcel(this.liquidationId!, file);
            if (uploaded) {
                this.excelUploaded = true;
                this.uploadedFileName = file.name;
                this.alertService.success('Archivo cargado correctamente.', 'Excel cargado');
            }
        } finally {
            this.isUploading = false;
            this.loaderService.hide();
        }
    }

    async onProcess(): Promise<void> {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }

        if (!this.excelReady) {
            this.showExcelError = true;
            return;
        }

        this.isProcessing = true;
        const { week, year, daysOfWeek } = this.form.value;

        try {
            const updated = await this.adminService.updateLiquidation(this.liquidationId!, { week, year, daysOfWeek }, false);
            if (!updated) return;

            const processed = await this.adminService.reprocessLiquidationExcel(this.liquidationId!);
            if (processed) {
                this.dialogRef.close(true);
            }
        } catch {
            this.alertService.error('Ocurrió un error durante el procesamiento.', 'Error');
        } finally {
            this.isProcessing = false;
        }
    }

    private uploadExcel(liquidationId: string, file: File): Promise<boolean> {
        return new Promise((resolve) => {
            const storage = getStorage();
            const filePath = `uploads/liquidations/${liquidationId}/${file.name}`;
            const storageRef = ref(storage, filePath);
            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on('state_changed',
                null,
                (_error) => {
                    this.alertService.error('Error al subir el archivo Excel.', 'Error');
                    resolve(false);
                },
                async () => {
                    const saved = await this.adminService.updateLiquidation(liquidationId, {
                        excelFilePath: filePath,
                        excelFileName: file.name
                    }, false);
                    resolve(saved);
                }
            );
        });
    }
}
